/**
 * Asset marketplace taxonomy — categories, Jamaica locations, post kinds.
 * Single source of truth for the Craigslist-style browse layer.
 */

export type PostKind = 'on-system' | 'off-system';

export type MarketplaceSubcategory = {
  id: string;
  label: string;
  postKind: PostKind;
};

export type MarketplaceCategory = {
  id: string;
  label: string;
  icon: string;
  postKind: PostKind;
  description: string;
  subcategories: MarketplaceSubcategory[];
};

export type JamaicaLocation = {
  id: string;
  label: string;
  parish?: string;
};

export const marketplaceBrowseCopy = {
  title: 'Jamaica Marketplace',
  subtitle: 'Pick a category, narrow by parish or town, then browse listings.',
  locationLabel: 'Area',
  allAreasLabel: 'All Jamaica',
  featuredLabel: 'Featured',
  recentLabel: 'Recent',
  browseAllLink: 'All categories',
  postListingCta: 'Post ad',
  emptyFiltered: 'No listings in this area yet. Try All Jamaica or post first.',
  sidebar: {
    postAd: 'post an ad',
    browseTitle: 'browse',
    allCategories: 'all categories',
    clearFilters: 'clear filters',
    goTitle: 'go to',
    home: 'home',
    runnerHub: 'runner cockpit',
    merchant: 'merchant suite',
    noticeboard: 'live noticeboard',
    areasTitle: 'areas',
    helpTitle: 'help',
    helpEscrow: 'Blue-dot categories settle through on-chain escrow.',
    helpCommunity: 'Green-dot posts are informational — contact off-platform.',
    helpSafety: 'Meet in public for handoffs. Use Handshake PIN for runner gigs.',
    legendTitle: 'legend',
    legendOnSystem: 'escrow / on-system',
    legendOffSystem: 'community / off-system',
  },
};

export const jamaicaLocations: JamaicaLocation[] = [
  { id: 'all', label: 'All Jamaica' },
  { id: 'kingston', label: 'Kingston', parish: 'Kingston' },
  { id: 'half-way-tree', label: 'Half Way Tree', parish: 'St. Andrew' },
  { id: 'constant-spring', label: 'Constant Spring', parish: 'St. Andrew' },
  { id: 'portmore', label: 'Portmore', parish: 'St. Catherine' },
  { id: 'spanish-town', label: 'Spanish Town', parish: 'St. Catherine' },
  { id: 'old-harbour', label: 'Old Harbour', parish: 'St. Catherine' },
  { id: 'may-pen', label: 'May Pen', parish: 'Clarendon' },
  { id: 'mandeville', label: 'Mandeville', parish: 'Manchester' },
  { id: 'christiana', label: 'Christiana', parish: 'Manchester' },
  { id: 'montego-bay', label: 'Montego Bay', parish: 'St. James' },
  { id: 'falmouth', label: 'Falmouth', parish: 'Trelawny' },
  { id: 'sav-la-mar', label: 'Savanna-la-Mar', parish: 'Westmoreland' },
  { id: 'negril', label: 'Negril', parish: 'Westmoreland' },
  { id: 'lucea', label: 'Lucea', parish: 'Hanover' },
  { id: 'ocho-rios', label: 'Ocho Rios', parish: 'St. Ann' },
  { id: 'st-anns-bay', label: "St. Ann's Bay", parish: 'St. Ann' },
  { id: 'browns-town', label: "Brown's Town", parish: 'St. Ann' },
  { id: 'port-antonio', label: 'Port Antonio', parish: 'Portland' },
  { id: 'port-maria', label: 'Port Maria', parish: 'St. Mary' },
  { id: 'annotto-bay', label: 'Annotto Bay', parish: 'St. Mary' },
  { id: 'morant-bay', label: 'Morant Bay', parish: 'St. Thomas' },
  { id: 'yallahs', label: 'Yallahs', parish: 'St. Thomas' },
  { id: 'linstead', label: 'Linstead', parish: 'St. Catherine' },
  { id: 'black-river', label: 'Black River', parish: 'St. Elizabeth' },
  { id: 'santa-cruz', label: 'Santa Cruz', parish: 'St. Elizabeth' },
];

export const marketplaceCategories: MarketplaceCategory[] = [
  {
    id: 'for-sale',
    label: 'For Sale',
    icon: 'Tag',
    postKind: 'on-system',
    description: 'Goods with on-chain escrow settlement',
    subcategories: [
      { id: 'electronics', label: 'Electronics', postKind: 'on-system' },
      { id: 'furniture', label: 'Furniture', postKind: 'on-system' },
      { id: 'appliances', label: 'Appliances', postKind: 'on-system' },
      { id: 'clothing', label: 'Clothing & accessories', postKind: 'on-system' },
      { id: 'vehicles', label: 'Vehicles & parts', postKind: 'on-system' },
      { id: 'farm-garden', label: 'Farm & garden', postKind: 'on-system' },
    ],
  },
  {
    id: 'services',
    label: 'Services',
    icon: 'Wrench',
    postKind: 'on-system',
    description: 'Bookable services settled through the protocol',
    subcategories: [
      { id: 'repairs', label: 'Repairs & maintenance', postKind: 'on-system' },
      { id: 'professional', label: 'Professional & business', postKind: 'on-system' },
      { id: 'lessons', label: 'Lessons & tutoring', postKind: 'on-system' },
      { id: 'creative', label: 'Creative & media', postKind: 'on-system' },
      { id: 'health', label: 'Health & wellness', postKind: 'on-system' },
    ],
  },
  {
    id: 'runner-pickup',
    label: 'Runner Pickup',
    icon: 'Truck',
    postKind: 'on-system',
    description: 'Field runner pickup and handoff gigs',
    subcategories: [
      { id: 'same-day', label: 'Same-day pickup', postKind: 'on-system' },
      { id: 'depot', label: 'Depot handoff', postKind: 'on-system' },
      { id: 'cold-chain', label: 'Cold chain', postKind: 'on-system' },
      { id: 'bulk', label: 'Bulk haul', postKind: 'on-system' },
    ],
  },
  {
    id: 'proxy-deliver',
    label: 'Proxy & Deliver',
    icon: 'Package',
    postKind: 'on-system',
    description: 'Proxy custody legs with escrow release',
    subcategories: [
      { id: 'door-to-door', label: 'Door-to-door proxy', postKind: 'on-system' },
      { id: 'cross-parish', label: 'Cross-parish relay', postKind: 'on-system' },
      { id: 'express', label: 'Express courier', postKind: 'on-system' },
    ],
  },
  {
    id: 'meet-proxy',
    label: 'Meet & Proxy',
    icon: 'Handshake',
    postKind: 'on-system',
    description: 'Verified meetup exchange with PIN handoff',
    subcategories: [
      { id: 'secure-meet', label: 'Secure meetup point', postKind: 'on-system' },
      { id: 'escrow-handoff', label: 'Escrow handoff', postKind: 'on-system' },
      { id: 'verified-exchange', label: 'Verified exchange', postKind: 'on-system' },
    ],
  },
  {
    id: 'housing',
    label: 'Housing',
    icon: 'Home',
    postKind: 'off-system',
    description: 'Sale, rent, and rooms — off-system informational posts',
    subcategories: [
      { id: 'for-sale', label: 'Homes for sale', postKind: 'off-system' },
      { id: 'for-rent', label: 'Rentals', postKind: 'off-system' },
      { id: 'rooms', label: 'Rooms & shares', postKind: 'off-system' },
      { id: 'commercial', label: 'Commercial space', postKind: 'off-system' },
      { id: 'land', label: 'Land & lots', postKind: 'off-system' },
    ],
  },
  {
    id: 'jobs',
    label: 'Jobs',
    icon: 'Briefcase',
    postKind: 'off-system',
    description: 'Employment posts — contact off-platform',
    subcategories: [
      { id: 'full-time', label: 'Full-time', postKind: 'off-system' },
      { id: 'part-time', label: 'Part-time', postKind: 'off-system' },
      { id: 'gig-work', label: 'Gig & contract', postKind: 'off-system' },
      { id: 'skilled-trades', label: 'Skilled trades', postKind: 'off-system' },
    ],
  },
  {
    id: 'events',
    label: 'Events & Meetups',
    icon: 'Calendar',
    postKind: 'off-system',
    description: 'Community events and gatherings',
    subcategories: [
      { id: 'concerts', label: 'Concerts & nightlife', postKind: 'off-system' },
      { id: 'community', label: 'Community events', postKind: 'off-system' },
      { id: 'sports', label: 'Sports & recreation', postKind: 'off-system' },
      { id: 'markets', label: 'Markets & fairs', postKind: 'off-system' },
    ],
  },
  {
    id: 'community',
    label: 'Community',
    icon: 'Users',
    postKind: 'off-system',
    description: 'General posts, activities, and local notices',
    subcategories: [
      { id: 'activities', label: 'Activities & groups', postKind: 'off-system' },
      { id: 'artists', label: 'Artists & musicians', postKind: 'off-system' },
      { id: 'classes', label: 'Classes & workshops', postKind: 'off-system' },
      { id: 'lost-found', label: 'Lost & found', postKind: 'off-system' },
      { id: 'general', label: 'General', postKind: 'off-system' },
    ],
  },
];

export const getCategoryById = (categoryId: string | null) =>
  marketplaceCategories.find(c => c.id === categoryId) || null;

export const getSubcategory = (categoryId: string | null, subcategoryId: string | null) => {
  const cat = getCategoryById(categoryId);
  if (!cat || !subcategoryId) {
    return null;
  }
  return cat.subcategories.find(s => s.id === subcategoryId) || null;
};

export const getLocationLabel = (locationId: string) =>
  jamaicaLocations.find(l => l.id === locationId)?.label || 'Jamaica';
